import { getSitemap, getAudits } from '../api';
import { normalizePageUrl, normalizeReferencePageUrl } from './referencePages';

/** @deprecated Préférer normalizePageUrl — conservé pour appels existants. */
export const normUrl = (u) => normalizePageUrl(u) || (u || '').trim().replace(/\/+$/, '');

function ensureHttpUrl(raw, projectUrl) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, '') || trimmed;
  }
  return normalizeReferencePageUrl(trimmed, projectUrl)
    || normalizeReferencePageUrl(`/${trimmed.replace(/^\//, '')}`, projectUrl);
}

/**
 * Construit les lignes d'affichage en fusionnant sitemap, pages de référence et audits.
 * Les URLs auditées hors sitemap (landing, pages manuelles) sont toujours incluses.
 */
/**
 * @param {'all'|'reference-only'|'none'} orphanAudits
 *   - reference-only (défaut) : hors sitemap, uniquement extraUrls (pages de référence)
 *   - all : toutes les URLs auditées en base (lourd sur grands projets)
 *   - none : sitemap + extraUrls uniquement
 */
export function buildAuditRows(
  sitemapUrls,
  audits,
  projectUrl,
  {
    missingLoading = false,
    prependHome = false,
    extraUrls = [],
    orphanAudits = 'reference-only',
  } = {},
) {
  const auditByNorm = new Map();
  (audits || []).forEach((a) => {
    if (!a?.url) return;
    const key = normalizePageUrl(a.url);
    if (!key) return;
    const existing = auditByNorm.get(key);
    if (
      !existing
      || (a.date_audit && (!existing.date_audit || a.date_audit > existing.date_audit))
    ) {
      auditByNorm.set(key, a);
    }
  });

  const extraNorm = new Set();
  for (const u of extraUrls || []) {
    const key = normalizePageUrl(ensureHttpUrl(u, projectUrl));
    if (key) extraNorm.add(key);
  }

  const seen = new Set();
  const urlsToUse = [];

  const addUrl = (raw) => {
    const href = ensureHttpUrl(raw, projectUrl);
    if (!href) return;
    const key = normalizePageUrl(href);
    if (!key || seen.has(key)) return;
    seen.add(key);
    urlsToUse.push(href);
  };

  const sitemapList = (sitemapUrls || []).filter(
    (u) => typeof u === 'string' && u.trim().length > 0,
  );
  const hasSitemap = sitemapList.length > 0;

  for (const u of sitemapList) addUrl(u);
  if (prependHome && projectUrl) addUrl(projectUrl);
  for (const u of extraUrls || []) addUrl(u);

  if (orphanAudits === 'all' || (!hasSitemap && orphanAudits !== 'none')) {
    for (const a of audits || []) addUrl(a.url);
  } else if (orphanAudits === 'reference-only') {
    for (const a of audits || []) {
      const key = normalizePageUrl(ensureHttpUrl(a.url, projectUrl));
      if (key && extraNorm.has(key) && !seen.has(key)) addUrl(a.url);
    }
  }

  const rows = urlsToUse.map((u) => {
    const audit = auditByNorm.get(normalizePageUrl(u));
    return audit
      ? { ...audit, url: u, loading: false }
      : { url: u, loading: missingLoading };
  });

  return { urls: urlsToUse, rows };
}

/**
 * Récupère sitemap + audits puis construit les lignes (fetch unique mutualisé).
 */
export async function fetchAuditRows(
  projectSlug,
  projectUrl,
  { cacheBust = false, missingLoading = false, prependHome = false, extraUrls = [] } = {},
) {
  const opts = cacheBust ? { cacheBust: true } : undefined;
  const [sitemap, audits] = await Promise.all([
    getSitemap(projectSlug, opts).catch(() => ({ urls: [] })),
    getAudits(projectSlug, opts).catch(() => []),
  ]);
  return buildAuditRows(sitemap.urls || [], audits, projectUrl, {
    missingLoading,
    prependHome,
    extraUrls,
  });
}
